import React, { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import { useSATCalculations } from '@/hooks/useSATCalculations'
import { socialShare } from '@/lib/socialShare'

// Official Digital SAT Math conversion (44 questions total)
const mathConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 230, 6: 250, 7: 270, 8: 290, 9: 310,
  10: 330, 11: 350, 12: 360, 13: 380, 14: 390, 15: 410, 16: 420, 17: 440, 18: 450,
  19: 460, 20: 470, 21: 480, 22: 490, 23: 500, 24: 510, 25: 520, 26: 530, 27: 540,
  28: 550, 29: 560, 30: 570, 31: 590, 32: 610, 33: 630, 34: 650, 35: 670, 36: 690,
  37: 710, 38: 730, 39: 750, 40: 760, 41: 770, 42: 780, 43: 790, 44: 800
}

// Official Digital SAT Reading & Writing conversion (54 questions total)
const readingConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 220, 6: 230, 7: 250, 8: 260, 9: 280,
  10: 290, 11: 300, 12: 310, 13: 320, 14: 330, 15: 340, 16: 350, 17: 360, 18: 370,
  19: 380, 20: 390, 21: 400, 22: 410, 23: 420, 24: 430, 25: 440, 26: 450, 27: 460,
  28: 470, 29: 480, 30: 490, 31: 500, 32: 510, 33: 520, 34: 530, 35: 540, 36: 550,
  37: 560, 38: 570, 39: 580, 40: 590, 41: 610, 42: 620, 43: 640, 44: 650, 45: 670,
  46: 680, 47: 700, 48: 710, 49: 730, 50: 740, 51: 760, 52: 770, 53: 780, 54: 800
}

export function PracticeTestCalculator() {
  const [selectedTest, setSelectedTest] = useState<string>('1')
  const [mathRaw, setMathRaw] = useState<string>('')
  const [readingRaw, setReadingRaw] = useState<string>('')

  const { saveCalculation } = useSATCalculations()

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('practiceTestData')
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setSelectedTest(data.selectedTest || '1')
        setMathRaw(data.mathRaw || '')
        setReadingRaw(data.readingRaw || '')
      } catch (e) {
        console.error('Failed to load saved data:', e)
      }
    }
  }, [])

  // Save to localStorage whenever values change
  useEffect(() => {
    const data = { selectedTest, mathRaw, readingRaw }
    localStorage.setItem('practiceTestData', JSON.stringify(data))
  }, [selectedTest, mathRaw, readingRaw])

  const calculateScore = (raw: string, conversion: { [key: number]: number }, maxRaw: number) => {
    const num = parseInt(raw)
    if (isNaN(num) || num < 0 || num > maxRaw) return 0
    return conversion[num] || 200
  }

  const mathScore = calculateScore(mathRaw, mathConversion, 44)
  const readingScore = calculateScore(readingRaw, readingConversion, 54)
  const totalScore = mathScore + readingScore

  const handleSaveCalculation = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to save your calculation')
      return
    }

    if (totalScore <= 400) {
      alert('Please enter valid scores')
      return
    }

    try {
      await saveCalculation({
        calculationType: 'practice_test',
        mathRaw: parseInt(mathRaw),
        readingRaw: parseInt(readingRaw),
        mathScaled: mathScore,
        readingScaled: readingScore,
        totalScore: totalScore,
        testNumber: `Test ${selectedTest}`
      })
      alert('Practice test score saved successfully!')
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save calculation')
    }
  }

  const handleEmailReport = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to email your report')
      return
    }

    if (totalScore <= 400) {
      alert('Please enter valid scores before emailing')
      return
    }

    try {
      await lumi.functions.invoke('sendScoreReport', {
        method: 'POST',
        body: {
          mathRaw: parseInt(mathRaw),
          readingRaw: parseInt(readingRaw),
          mathScaled: mathScore,
          readingScaled: readingScore,
          totalScore: totalScore,
          calculationType: 'practice_test',
          testNumber: `Test ${selectedTest}`
        }
      })

      alert(`Practice test report sent to ${user.email}!`)
    } catch (error: any) {
      console.error('Failed to send email:', error)
      const errorMessage = error?.data?.error || 'Failed to send email report'
      alert(errorMessage)
    }
  }

  const handleDownloadPDF = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to download your report')
      return
    }

    if (totalScore <= 400) {
      alert('Please enter valid scores before downloading')
      return
    }

    try {
      const result = await lumi.functions.invoke('generatePDFReport', {
        method: 'POST',
        body: {
          mathRaw: parseInt(mathRaw),
          readingRaw: parseInt(readingRaw),
          mathScaled: mathScore,
          readingScaled: readingScore,
          totalScore: totalScore,
          calculationType: 'practice_test',
          testNumber: `Test ${selectedTest}`
        }
      })

      const blob = new Blob([result], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `SAT-Practice-Test-${selectedTest}-Report-${totalScore}.pdf`
      a.click()
      URL.revokeObjectURL(url)

      alert('PDF report downloaded successfully!')
    } catch (error: any) {
      console.error('Failed to generate PDF:', error)
      const errorMessage = error?.data?.error || 'Failed to generate PDF report'
      alert(errorMessage)
    }
  }

  const handleCopyLink = async () => {
    const shareData = {
      totalScore,
      calculationType: 'practice_test',
      mathScore,
      readingScore,
      testNumber: `Test ${selectedTest}`
    }
    const success = await socialShare.copyLink(shareData)
    if (success) {
      alert('Link copied to clipboard!')
    } else {
      alert('Failed to copy link')
    }
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-6 md:p-8" aria-labelledby="practice-test-heading">
      <h2 id="practice-test-heading" className="text-3xl font-bold text-gray-900 mb-2">
        SAT Practice Test Score Calculator
      </h2>
      <p className="text-gray-600 mb-6">
        Calculate your score from official College Board practice tests. Select your practice test and enter your raw scores to see your estimated SAT score.
      </p>

      {/* Practice Test Selector */}
      <div className="mb-6">
        <label htmlFor="practice-test-select" className="block text-sm font-medium text-gray-700 mb-2">
          Select Your Practice Test
        </label>
        <select
          id="practice-test-select"
          value={selectedTest}
          onChange={(e) => setSelectedTest(e.target.value)}
          className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-describedby="practice-test-hint"
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
            <option key={num} value={num}>
              SAT Practice Test {num}
            </option>
          ))}
        </select>
        <p id="practice-test-hint" className="text-sm text-gray-500 mt-2">
          Choose from official College Board practice tests 1-10
        </p>
      </div>

      {/* Score Input */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div>
          <label htmlFor="practice-math-raw" className="block text-sm font-medium text-gray-700 mb-2">
            Math Raw Score (0-44)
          </label>
          <input
            id="practice-math-raw"
            type="number"
            min="0"
            max="44"
            value={mathRaw}
            onChange={(e) => setMathRaw(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter number correct (0-44)"
            aria-describedby="practice-math-hint"
          />
          <p id="practice-math-hint" className="text-sm text-gray-500 mt-1">
            Scaled Score: {mathScore > 0 ? mathScore : '---'}
          </p>
        </div>

        <div>
          <label htmlFor="practice-reading-raw" className="block text-sm font-medium text-gray-700 mb-2">
            Reading & Writing Raw Score (0-54)
          </label>
          <input
            id="practice-reading-raw"
            type="number"
            min="0"
            max="54"
            value={readingRaw}
            onChange={(e) => setReadingRaw(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter number correct"
            aria-describedby="practice-reading-hint"
          />
          <p id="practice-reading-hint" className="text-sm text-gray-500 mt-1">
            Scaled Score: {readingScore > 0 ? readingScore : '---'}
          </p>
        </div>
      </div>

      {/* Results */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            Practice Test {selectedTest} Score
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSaveCalculation}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              aria-label="Save practice test score"
            >
              💾 Save
            </button>
            <button
              onClick={handleEmailReport}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              aria-label="Email practice test report"
            >
              📧 Email
            </button>
            <button
              onClick={handleDownloadPDF}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              aria-label="Download PDF practice test report"
            >
              📄 PDF
            </button>
          </div>
        </div>
        
        {/* Social Share Buttons */}
        {totalScore > 400 && (
          <div className="flex flex-wrap justify-center gap-2 mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={() => socialShare.twitter({
                totalScore,
                calculationType: 'practice_test',
                mathScore,
                readingScore,
                testNumber: `Test ${selectedTest}`
              })}
              className="px-3 py-1.5 bg-black text-white rounded hover:bg-gray-800 transition-colors text-xs font-medium"
              aria-label="Share on Twitter"
            >
              𝕏 Share
            </button>
            <button
              onClick={() => socialShare.facebook({
                totalScore,
                calculationType: 'practice_test'
              })}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium"
              aria-label="Share on Facebook"
            >
              📘 Facebook
            </button>
            <button
              onClick={() => socialShare.linkedin({
                totalScore,
                calculationType: 'practice_test',
                mathScore,
                readingScore,
                testNumber: `Test ${selectedTest}`
              })}
              className="px-3 py-1.5 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors text-xs font-medium"
              aria-label="Share on LinkedIn"
            >
              💼 LinkedIn
            </button>
            <button
              onClick={() => socialShare.whatsapp({
                totalScore,
                calculationType: 'practice_test',
                mathScore,
                readingScore,
                testNumber: `Test ${selectedTest}`
              })}
              className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs font-medium"
              aria-label="Share on WhatsApp"
            >
              💬 WhatsApp
            </button>
            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-xs font-medium"
              aria-label="Copy link to clipboard"
            >
              🔗 Copy Link
            </button>
          </div>
        )}
        
        <div className="text-center">
          <div className="text-5xl font-bold text-blue-600 mb-2">
            {totalScore > 400 ? totalScore : '---'}
          </div>
          <div className="text-sm text-gray-600">Total Score (400-1600)</div>
          {totalScore > 400 && (
            <div className="mt-4 text-sm text-gray-600">
              Math: <span className="font-semibold text-gray-900">{mathScore}</span> | 
              Reading & Writing: <span className="font-semibold text-gray-900">{readingScore}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tips Section */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-gray-900 mb-2">💡 Practice Test Tips</h4>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li>Take practice tests under timed conditions for accurate results</li>
          <li>Review incorrect answers to identify weak areas</li>
          <li>Track your scores over multiple tests to measure improvement</li>
          <li>Focus on consistent practice rather than cramming</li>
        </ul>
      </div>
    </section>
  )
}
